import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  DataAvailableDateRange,
  DataService,
  SensorStatus,
  SensorsSensor,
  SensorsSensorfield,
} from 'src/app/api/generated';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import {
  formatAlarmComparison,
  formatAlarmDescriptor,
  formatMaximumSeverity,
  getAlarmLevelStatusLabel,
} from '../alarm-display';

@Component({
    selector: 'sensor-details-dialog',
    templateUrl: './sensor-details-dialog.component.html',
    styleUrls: ['./sensor-details-dialog.component.scss'],
    standalone: false
})
export class SensorDetailsDialogComponent implements OnInit, OnDestroy {
  loading = true;
  fieldLoading: Record<string, boolean> = {};
  fieldError: Record<string, string | null> = {};
  latestByField: Record<string, { epoch: string | null }> = {};
  readonly formatAlarmComparison = formatAlarmComparison;
  readonly formatAlarmDescriptor = formatAlarmDescriptor;
  readonly formatMaximumSeverity = formatMaximumSeverity;
  readonly getAlarmLevelStatusLabel = getAlarmLevelStatusLabel;

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<SensorDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { sensor: SensorsSensor; status?: SensorStatus | null },
    private dataService: DataService
  ) {}

  getStatusLabel(status?: SensorStatus.StatusEnum): string {
    switch (status) {
      case 'alarm_level_2':
        return 'Alarm (Stufe 2)';
      case 'alarm_level_3':
        return 'Alarm (Stufe 3)';
      case 'stale':
        return 'Keine aktuellen Daten';
      case 'warning':
        return 'Warnung';
      case 'ok':
        return 'OK';
      default:
        return 'Nicht verfügbar';
    }
  }

  getDataStatusLabel(
    status: SensorStatus.DataStatusEnum | SensorStatus.MeasurementStatusEnum
  ): string {
    switch (status) {
      case 'current':
      case 'available':
        return 'Aktuell';
      case 'stale':
        return 'Veraltet';
      case 'missing':
        return 'Keine Messung vorhanden';
      case 'unavailable':
        return 'Nicht verfügbar';
    }
  }

  ngOnInit(): void {
    const sensor = this.data.sensor;
    const fields: SensorsSensorfield[] = sensor.available_fields || [];

    // Initialize per-field states
    for (const f of fields) {
      this.fieldLoading[f.identifier] = true;
      this.fieldError[f.identifier] = null;
      this.latestByField[f.identifier] = { epoch: null };
    }

    const stationId = sensor.influx_name;

    const perFieldRequests = fields.map((field) =>
      this.dataService
        .dataDaterangeInfluxList(String(stationId), field.identifier)
        .pipe(
          map((range: DataAvailableDateRange) => ({
            fieldId: field.identifier,
            epoch: range?.end_date ?? null,
          })),
          catchError(() => {
            this.fieldError[field.identifier] = 'Failed to load';
            return of({ fieldId: field.identifier, epoch: null });
          }),
          takeUntil(this.destroy$)
        )
    );

    if (perFieldRequests.length === 0) {
      this.loading = false;
      return;
    }

    forkJoin(perFieldRequests)
      .pipe(takeUntil(this.destroy$))
      .subscribe((results) => {
        for (const r of results) {
          this.latestByField[r.fieldId] = { epoch: r.epoch };
          this.fieldLoading[r.fieldId] = false;
        }
        this.loading = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
